import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import Image from 'next/image'
import { useMediaQuery } from '@mantine/hooks'
import Slider, { Settings } from 'react-slick'
import styles from './kdt-design-5th.module.scss'

interface IProjectRenderProps {
  contents: {
    category: string
    titlePc: string
    titleM: string
    contents: {
      contentsId: string
      contentsIntro?: string
      contentsInnerType?: string
      contentsInner: string[]
      contentsImgUrlPc?: string
      contentsImgUrlM?: string
    }[]
  }[]
}

const projectCustomPaging = (i: number) => {
  const dotLabels = ['개인 프로젝트', '팀 프로젝트 1', '팀 프로젝트 2']
  return (
    <button>
      <span className="text-center">{dotLabels[i]}</span>
    </button>
  )
}

export const ProjectSliderItem = ({ contents }: IProjectRenderProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const settings = {
    dots: true,
    infinite: false,
    variableWidth: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: false,
    touchMove: true,
    arrows: true,
    adaptiveHeight: true,
    customPaging: projectCustomPaging,
  }

  return (
    <div>
      <Slider {...settings}>
        {contents.map(({ category, titleM, titlePc, contents }) => (
          <div className="w-full overflow-hidden pt-[52px] lg:pt-[88px]" key={titlePc}>
            <div className="flex flex-col items-center overflow-hidden border border-black bg-white px-4 py-5 lg:px-[105px] lg:py-10">
              <div className="flex flex-col items-center justify-center">
                <p className="mb-2 max-w-fit bg-[rgba(255,119,16,0.1)] px-2 py-1 text-[10px] font-semibold text-yellow-450 lg:mb-4 lg:px-[10.5px] lg:py-[9px] lg:text-base">
                  {category}
                </p>
                <h5 className="whitespace-pre-line text-center text-base font-bold leading-normal text-label-normal lg:text-2xl">
                  {isMobile ? titleM : titlePc}
                </h5>
              </div>
              <div className="mt-6 w-full lg:mt-12">
                {contents.map(
                  ({
                    contentsId,
                    contentsIntro,
                    contentsInnerType,
                    contentsInner,
                    contentsImgUrlPc,
                    contentsImgUrlM,
                  }) => (
                    <div key={contentsId}>
                      <div className="flex flex-col gap-2.5 bg-alternative p-4 lg:flex-row lg:gap-6 lg:px-6 lg:py-5">
                        <div className="flex flex-col lg:w-1/2 lg:flex-1">
                          <h6 className="mb-3 whitespace-pre-line border-b border-normal pb-3 text-sm font-semibold text-label-normal lg:text-lg">
                            {contentsIntro}
                          </h6>
                          {contentsInner && (
                            <div
                              className={`${contentsInnerType === 'custom' ? styles.section6_ul : ''} flex w-full flex-col gap-0.5 lg:gap-0`}
                            >
                              {contentsInner.map(innerContent => (
                                <p
                                  key={innerContent}
                                  className={`${styles.text_dot} ${styles.text_dot_section6} whitespace-pre-line text-sm leading-normal text-label-normal lg:text-base`}
                                >
                                  {innerContent}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="relative w-full overflow-hidden lg:w-auto lg:max-w-[477px] lg:flex-1">
                          <Image
                            src={
                              isMobile ? (contentsImgUrlM as string) : (contentsImgUrlPc as string)
                            }
                            layout="fill"
                            objectFit="cover"
                            className="!relative"
                            alt={titlePc}
                          />
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}

interface IProjectOutputProps {
  contents: {
    imgUrlPc: string
    imgUrlM: string
    title: string
  }[]
}

export const ProjectOutputSliderItem = ({ contents }: IProjectOutputProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: true,
    centerPadding: '34%',
    touchMove: true,
    variableWidth: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          dots: true,
          arrows: true,
          infinite: true,
          slidesToShow: 1,
          centerPadding: '24px',
        },
      },
    ],
  }
  return (
    <Slider {...settings}>
      {contents.map(({ imgUrlPc, imgUrlM, title }) => (
        <div key={title} className="px-1 pb-5 lg:px-6 lg:pb-[88px]">
          <div className="w-full overflow-hidden lg:border-[2px] lg:border-[#00E0FF]">
            <div className="relative w-full border-[2px] border-[#00E0FF] pb-[51.28%] lg:border-0 lg:pb-[56.29%]">
              <Image
                src={isMobile ? imgUrlM : imgUrlPc}
                layout="fill"
                objectFit="cover"
                className="!absolute left-0 top-0"
                alt={title}
                unoptimized
              />
            </div>
            <div className="flex flex-row items-center justify-center py-3 text-sm text-label-assistive lg:bg-[#404040] lg:py-[35px] lg:text-xl lg:font-semibold lg:text-white">
              {title}
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface IClassOneRenderProps {
  contents: {
    category: string
    titlePc: string
    titleM: string
    contents: {
      contentsId: string
      contentsInner: string[]
    }[]
  }[]
}

const class1CustomPaging = (i: number) => {
  const dotLabels = ['1회차', '2회차', '3회차']
  return (
    <button>
      <span className="text-center">{dotLabels[i]}</span>
    </button>
  )
}

export const ClassOneSliderItem = ({ contents }: IClassOneRenderProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const settings = {
    dots: true,
    infinite: false,
    variableWidth: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: false,
    touchMove: true,
    arrows: true,
    adaptiveHeight: true,
    customPaging: class1CustomPaging,
  }

  return (
    <div>
      <Slider {...settings}>
        {contents.map(({ category, titleM, titlePc, contents }) => (
          <div className="w-full overflow-hidden pt-[52px] lg:pt-[88px]" key={titlePc}>
            <div className="flex flex-col items-center overflow-hidden border border-black bg-white px-4 py-5 lg:px-[105px] lg:py-10">
              <div className="flex flex-col items-center justify-center">
                <p className="mb-2 max-w-fit bg-[rgba(255,119,16,0.1)] px-2 py-1 text-[10px] font-semibold text-yellow-450 lg:mb-4 lg:px-[10.5px] lg:py-[9px] lg:text-base">
                  {category}
                </p>
                <h5 className="whitespace-pre-line text-center text-base font-bold leading-normal text-label-normal lg:text-2xl">
                  {isMobile ? titleM : titlePc}
                </h5>
              </div>
              <div className="mt-6 w-full lg:mt-12">
                {contents.map(({ contentsId, contentsInner }) => (
                  <div key={contentsId}>
                    <div className="flex flex-col lg:bg-alternative lg:px-6 lg:py-5">
                      {contentsInner &&
                        contentsInner.map(innerContent => (
                          <p
                            key={innerContent}
                            className={`${styles.text_dot} ${styles.text_dot_section8} whitespace-pre-wrap text-sm leading-normal text-label-normal lg:border-b lg:border-normal lg:py-3 lg:text-base lg:first-of-type:pt-0 lg:last-of-type:border-0 lg:last-of-type:pb-0`}
                          >
                            {innerContent}
                          </p>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}

interface IClassTwoRenderProps {
  contents: {
    titlePc: string
    titleM: string
    imgUrlPc: string
    imgUrlM: string
    contents: {
      contentsId: string
      contentsIntro?: string
      contentsInner: string[]
    }[]
  }[]
}

const class2CustomPaging = (i: number) => {
  const dotLabels = ['APP 디자인', 'WEB 디자인']
  return (
    <button>
      <span className="text-center">{dotLabels[i]}</span>
    </button>
  )
}

export const ClassTwoSliderItem = ({ contents }: IClassTwoRenderProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const settings = {
    dots: true,
    infinite: false,
    variableWidth: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: false,
    touchMove: true,
    arrows: true,
    adaptiveHeight: true,
    customPaging: class2CustomPaging,
  }

  return (
    <div>
      <Slider {...settings}>
        {contents.map(({ titleM, titlePc, imgUrlM, imgUrlPc, contents }) => (
          <div className="w-full overflow-hidden pt-[52px] lg:pt-[88px]" key={titlePc}>
            <div className="flex flex-col items-center overflow-hidden border border-black bg-white px-4 py-5 lg:px-[105px] lg:py-10">
              <div className="flex flex-col items-center justify-center">
                <h5 className="whitespace-pre-line text-center text-base font-bold leading-normal text-label-normal lg:text-2xl">
                  {isMobile ? titleM : titlePc}
                </h5>
              </div>
              <div className="mt-6 w-full lg:mt-12">
                <div className="relative mb-6 w-full overflow-hidden lg:hidden">
                  <Image
                    src={isMobile ? (imgUrlM as string) : (imgUrlPc as string)}
                    layout="fill"
                    objectFit="cover"
                    className="!relative"
                    alt={titlePc}
                  />
                </div>

                {contents.map(({ contentsId, contentsIntro, contentsInner }) => (
                  <div key={contentsId} className="flex flex-col">
                    <h6 className="mb-2 whitespace-pre-line border-normal text-sm font-semibold text-label-normal lg:mb-6 lg:whitespace-normal lg:text-xl">
                      {contentsIntro}
                    </h6>
                    <div className="flex w-full flex-col lg:flex-row lg:gap-6 lg:bg-alternative lg:px-6 lg:py-5">
                      <div className="flex w-full flex-col lg:w-1/2 lg:flex-1">
                        {contentsInner && (
                          <div className="flex w-full flex-col gap-0.5 lg:gap-0">
                            {contentsInner.map(innerContent => (
                              <p
                                key={innerContent}
                                className={`${styles.text_dot} ${styles.text_dot_section9} whitespace-pre-wrap text-xs leading-normal text-label-normal lg:border-b lg:border-normal lg:py-3 lg:text-base lg:first-of-type:pt-0 lg:last-of-type:border-0 lg:last-of-type:pb-0`}
                              >
                                {innerContent}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="relative hidden w-full overflow-hidden lg:inline-block lg:w-auto lg:max-w-[477px] lg:flex-1">
                        <Image
                          src={isMobile ? (imgUrlM as string) : (imgUrlPc as string)}
                          layout="fill"
                          objectFit="cover"
                          className="!relative"
                          alt={titlePc}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}

interface IClassSupportProps {
  contents: {
    imgUrlPc: string
    imgUrlM: string
    type: string
    title: string
    desc: string
    descList: string[]
  }[]
}

export const ClassSupportSliderItem = ({ contents }: IClassSupportProps) => {
  const settings: Settings = {
    dots: true,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: true,
    centerPadding: '24px',
    touchMove: true,
    variableWidth: false,
  }

  return (
    <Slider {...settings}>
      {contents.map(({ imgUrlM, type, title, desc, descList }) => (
        <div key={title} className="px-1 pb-10">
          <div className="h-[566px] w-full overflow-hidden border border-black bg-white">
            <div className="relative w-full">
              <Image
                src={imgUrlM}
                layout="fill"
                objectFit="cover"
                className="!relative w-full"
                alt={title}
              />
            </div>
            <div className="flex flex-col border-t border-black bg-white px-4 py-4">
              <p className="max-w-fit bg-[rgba(255,119,16,0.10)] px-2 py-1 text-xs font-bold text-yellow-450">
                {type}
              </p>
              <h6 className="my-3 whitespace-pre-line text-lg font-bold leading-normal text-label-normal">
                {title}
              </h6>
              <p className="whitespace-pre-line text-base font-medium text-label-normal">{desc}</p>
              <div className="mt-2">
                {descList.map(item => (
                  <p
                    key={`${title}-${item}`}
                    className={`${styles.text_dot} ${styles.text_dot_section11} whitespace-pre-line text-sm leading-normal text-label-neutral`}
                  >
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface IBenefitCarouselProps {
  contents: {
    imgUrlM: string
    imgUrlPc: string
    title: string
    desc: string
    option?: string
  }[]
}

export const BenefitSliderItem = ({ contents }: IBenefitCarouselProps) => {
  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    touchMove: true,
    variableWidth: true,
    centerMode: false,
    centerPadding: '0',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          infinite: true,
          slidesToShow: 1,
          dots: true,
          arrows: false,
          centerMode: false,
          centerPadding: '0',
          variableWidth: true,
        },
      },
    ],
  }
  return (
    <Slider {...settings}>
      {contents.map(({ desc, title, imgUrlM, option }) => (
        <div key={title} className="w-full pb-16">
          <div className="pl-[18px]">
            <div className="relative w-full overflow-hidden">
              <Image
                src={imgUrlM}
                layout="fill"
                objectFit="cover"
                className="!relative"
                alt={title}
              />
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <p className="ext-xl font-bold text-white">{title}</p>
              <p className="text-base leading-normal text-white">{desc}</p>
              {option && <p className="text-xs text-label-neutral">{option}</p>}
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface IReviewProps {
  contents: {
    imgUrlM: string
    imgUrlPc: string
    title: string
    desc: string
  }[]
}

export const ReviewSliderItem = ({ contents }: IReviewProps) => {
  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    touchMove: true,
    variableWidth: true,
    centerMode: false,
    centerPadding: '0',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          infinite: true,
          slidesToShow: 1,
          dots: true,
          arrows: false,
          centerMode: false,
          centerPadding: '0',
          variableWidth: true,
        },
      },
    ],
  }
  return (
    <Slider {...settings}>
      {contents.map(({ desc, title, imgUrlM }) => (
        <div key={title} className="w-full pb-16">
          <div className="pl-[18px]">
            <div className="flex flex-col gap-4 border border-[#00E0FF] bg-[#303030] p-4">
              <div className="flex flex-row items-start gap-4 border-b border-[#404040] pb-4">
                <div className="h-12 w-12 shrink-0 overflow-hidden">
                  <Image
                    src={imgUrlM}
                    layout="fill"
                    objectFit="cover"
                    className="!relative"
                    alt={title}
                  />
                </div>
                <p className="flex-1 whitespace-pre-line text-lg font-bold leading-normal text-white">
                  {title}
                </p>
              </div>
              <p className="text-base leading-normal text-label-assistive">{desc}</p>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}
